import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  Clock,
  WifiOff,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/config/api';
import { BO_BASE } from '@/utils/getBasePath';

const bo = (route = '') => (route ? `${BO_BASE}/${route}` : BO_BASE || '/');

/**
 * Tile that surfaces fleet-wide uptime % and MTTR computed from the
 * existing `kiosk_incidents` history (no new polling).  Polls at most
 * every 5 minutes.
 */
export default function KioskReliabilityTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const token =
        localStorage.getItem('backoffice_token') ||
        localStorage.getItem('merchant_token') ||
        localStorage.getItem('access_token');
      if (!token) {
        setError('not_authenticated');
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API}/owner/kiosks/reliability?range=7d`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data?.summary || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'unknown');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const uptime = data?.uptime_pct;
  const mttr = data?.mttr_minutes;
  const unresolved = data?.unresolved_incident_count ?? 0;

  // Color thresholds per spec: >99 green, 95-99 amber, <95 rose
  let uptimeColor = 'text-slate-500';
  if (uptime != null) {
    if (uptime > 99) uptimeColor = 'text-emerald-600';
    else if (uptime >= 95) uptimeColor = 'text-amber-600';
    else uptimeColor = 'text-rose-600';
  }

  return (
    <Card
      data-testid="kiosk-reliability-tile"
      className="bg-white border-0 shadow-sm"
    >
      <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2">
            <Activity className="h-4 w-4 text-slate-500" />
            Kiosk Reliability
          </CardTitle>
          <button
            type="button"
            onClick={load}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Refresh reliability"
            data-testid="kiosk-reliability-tile-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
        {error ? (
          <div className="py-4 text-center">
            <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto mb-1" />
            <p className="text-xs text-slate-500">
              {error === 'not_authenticated'
                ? 'Sign in to view reliability'
                : 'Unable to load reliability'}
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400">
                7-day fleet uptime
              </p>
              <p
                data-testid="kiosk-reliability-uptime-value"
                className={`text-4xl font-bold ${uptimeColor}`}
              >
                {uptime != null ? `${uptime.toFixed(2)}%` : '—'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Stat
                testId="kiosk-reliability-stat-mttr"
                icon={<Clock className="h-4 w-4 text-slate-500" />}
                label="Avg recovery"
                value={mttr != null ? `${Math.round(mttr)}m` : '—'}
              />
              <Stat
                testId="kiosk-reliability-stat-unresolved"
                icon={<WifiOff className="h-4 w-4 text-rose-500" />}
                label="Active incidents"
                value={unresolved}
                valueClass={unresolved > 0 ? 'text-rose-600' : 'text-slate-700'}
              />
            </div>
            <Button
              variant="ghost"
              className="w-full mt-3 justify-between text-sm"
              onClick={() => navigate(bo('kiosks/reliability'))}
              data-testid="kiosk-reliability-open-page-button"
            >
              <span>Open reliability dashboard</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, icon, valueClass = 'text-slate-800', testId }) {
  return (
    <div
      data-testid={testId}
      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
    </div>
  );
}
